import {skipToken} from '@reduxjs/toolkit/query';
import {Action, ColorSchemeType, DataStatus, showNotification} from 'platform/components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetTagsForEntityQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetVehicleQuery} from '@dms/api/vehicle';
import {useRemoveWorkshopInterestVehicleMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError, useWorkshopInterestEditPermissions, VehicleWidget} from '@dms/shared';

import {buildArray, composePath, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface SelectedVehicleProps extends RequiredTestIdProps {
  vehicleId: string;
  interestId: string;
}

export function SelectedVehicle(props: SelectedVehicleProps) {
  const {
    data: vehicle,
    isLoading,
    isError,
  } = useGetVehicleQuery(props.vehicleId ? {vehicleId: props.vehicleId} : skipToken);

  const {data: entityTags} = useGetTagsForEntityQuery(
    props.vehicleId ? {recordId: props.vehicleId, resourceId: EntityResourceIds.vehicle} : skipToken
  );

  const [removeVehicle, {isLoading: isRemoving}] = useRemoveWorkshopInterestVehicleMutation();

  const {hasRemoveVehiclePermission} = useWorkshopInterestEditPermissions({
    interestId: props.interestId,
  });

  const handleRemoveVehicle = () => {
    removeVehicle({id: props.interestId})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError);
  };

  if (!vehicle) {
    return <DataStatus isLoading={isLoading} isError={isError} minHeight={50} />;
  }

  return (
    <VehicleWidget
      isBoxed
      flags={entityTags?.map((tag) => {
        const [parsedColor, subtle] = tag.valueColor.split('.') as [
          ColorSchemeType,
          string | undefined,
        ];
        return {label: tag.valueName, colorScheme: parsedColor, isSubtle: !isNilOrEmpty(subtle)};
      })}
      vehicle={{
        id: vehicle.id,
        vin: vehicle.vin,
        registrationPlate: vehicle.registrationPlate,
        make: vehicle.make,
        modelFamily: vehicle.modelFamily,
        trim: vehicle.trim,
        title: vehicle.title,
        variant: vehicle.variant,
        vehicleType: vehicle.type?.toString(),
        firstRegistrationOn: vehicle.firstRegistrationOn,
        manufacturedOn: vehicle.manufacturedOn,
        mileage: vehicle.mileage,
        fuelType: vehicle.fuelType,
        transmission: vehicle.transmission,
        bodyStyle: vehicle.bodyStyle,
        driveType: vehicle.drive,
        actions: buildArray<Action>()
          .add({
            type: 'button',
            title: i18n.t('entity.vehicle.labels.detail'),
            variant: 'outlined',
            leftIcon: 'action/launch',
            onClick: () => {
              window.open(composePath(vehiclesRoutes.detail, {params: {id: vehicle.id}}), '_blank');
            },
          })
          .add({
            type: 'button',
            title: i18n.t('general.actions.remove'),
            variant: 'outlined',
            leftIcon: 'action/delete',
            isLoading: isRemoving,
            isDisabled: !hasRemoveVehiclePermission,
            onClick: handleRemoveVehicle,
          }),
      }}
      data-testid={suffixTestId('vehicleWidget', props)}
    />
  );
}
